import React, { useState, createContext, useContext } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Tabs Context for managing active tab state
 */
const TabsContext = createContext(null);

/**
 * Tabs Root Component
 * @param {Object} props
 * @param {string} props.defaultValue - Default active tab value
 * @param {string} props.value - Controlled active tab value
 * @param {Function} props.onValueChange - Callback when tab changes
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Tab components
 */
export const Tabs = ({ defaultValue, value, onValueChange, className, children }) => {
  const [activeTab, setActiveTab] = useState(defaultValue || '');
  const { theme } = useTheme();

  const currentValue = value !== undefined ? value : activeTab;

  const handleTabChange = (newValue) => {
    if (value === undefined) {
      setActiveTab(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ activeTab: currentValue, setActiveTab: handleTabChange }}>
      <div className={cn("w-full", className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

/**
 * TabsList Component - Container for tab triggers
 * @param {Object} props
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - TabsTrigger components
 */
export const TabsList = ({ className, children }) => {
  const { theme } = useTheme();

  return (
    <div 
      className={cn(
        "flex border-b",
        theme.border.light,
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * TabsTrigger Component - Individual tab button
 * @param {Object} props
 * @param {string} props.value - Tab identifier value
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the tab is disabled
 * @param {React.ReactNode} props.children - Tab label content
 */
export const TabsTrigger = ({ value, className, disabled = false, children }) => {
  const { activeTab, setActiveTab } = useContext(TabsContext);
  const { theme } = useTheme();
  const isActive = activeTab === value;

  return (
    <button
      type="button"
      onClick={() => !disabled && setActiveTab(value)}
      disabled={disabled}
      className={cn(
        "px-4 py-2 text-sm font-medium transition-all duration-200",
        "border-b-2 border-transparent",
        "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
        isActive && "border-blue-500",
        isActive ? theme.text.primary : theme.text.secondary,
        !disabled && !isActive && `hover:${theme.text.primary}`,
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {children}
    </button>
  );
};

/**
 * TabsContent Component - Content panel for each tab
 * @param {Object} props
 * @param {string} props.value - Tab identifier value
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Tab content
 */
export const TabsContent = ({ value, className, children }) => {
  const { activeTab } = useContext(TabsContext);

  if (activeTab !== value) return null;

  return (
    <div 
      className={cn(
        "mt-4",
        "focus:outline-none",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * Example usage:
 * 
 * <Tabs defaultValue="tab1" onValueChange={(value) => console.log(value)}>
 *   <TabsList>
 *     <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *     <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 *     <TabsTrigger value="tab3" disabled>Tab 3 (Disabled)</TabsTrigger>
 *   </TabsList>
 *   
 *   <TabsContent value="tab1">
 *     <div>Content for Tab 1</div>
 *   </TabsContent>
 *   
 *   <TabsContent value="tab2">
 *     <div>Content for Tab 2</div>
 *   </TabsContent>
 * </Tabs>
 */

// Default export for backward compatibility
const TabsComponent = {
  Root: Tabs,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent
};

export default TabsComponent;
